import { action, computed, makeObservable, override } from "mobx"
import { PaginationProps, Typography } from "antd"

import { Pagination, PaginationOrderEnum } from "@src/shared/api/generated"
import Collection, {
  CollectionStateType,
} from "@src/shared/classes/Collection/Collection"
import {
  getCollectionMock,
  isCollectionMockModeEnabled,
} from "@src/shared/classes/Collection/collectionMocks"

const DEFAULT_PAGE_SIZE = 10

const defaultPagination = {
  limit: DEFAULT_PAGE_SIZE,
  maxTotal: 0,
  offset: 0,
  order: PaginationOrderEnum.Desc,
  total: 0,
}

export type CollectionWithPaginationState<T> = CollectionStateType<T> & {
  abortController: AbortController
  pagination: Pagination & {
    maxTotal: number
  }
  prevRequest: null | unknown
}
export abstract class CollectionWithPagination<T> extends Collection<T> {
  protected mockKey?: string

  state: CollectionWithPaginationState<T> = {
    abortController: new AbortController(),
    items: [],
    pagination: {
      ...defaultPagination,
    },
    prevRequest: null,
  }
  constructor() {
    super()

    makeObservable(this, {
      load: action.bound,
      order: computed,
      pageSize: computed,
      pagination: computed,
      setPagination: action.bound,
      state: override,
    })
  }
  private _getSignal() {
    this.state.abortController = new AbortController()

    return this.state.abortController.signal
  }
  private get abortController(): AbortController {
    return this.state.abortController
  }
  private getMockResponse(req: unknown) {
    if (!isCollectionMockModeEnabled() || !this.mockKey) {
      return null
    }

    const mock = getCollectionMock<T>(this.mockKey)

    if (!mock) {
      return null
    }

    return mock(req, {
      calcOffset: (page) => this.calcOffset(page),
      pageSize: this.pageSize,
    })
  }
  abortRequest() {
    if (this.abortController) {
      this.abortController.abort()
    }
  }
  protected calcOffset(page: number = 0) {
    return page > 0 ? (page - 1) * this.pageSize : page
  }
  async changePage(page: number) {
    if (this.state.prevRequest) {
      return this.load({ ...this.state.prevRequest, page })
    }

    return null
  }
  async load(req: unknown = {}) {
    this.abortRequest()

    const response =
      this.getMockResponse(req) ?? (await this.fetch(req, this._getSignal()))

    if (response) {
      const { data, pagination } = response

      this.replace(data)

      this.setPagination(pagination)

      this.state.prevRequest = { ...(req as object) }
    }
  }
  setPagination(page: Partial<Pagination> = {}) {
    this.state.pagination = {
      ...this.state.pagination,
      ...page,
      maxTotal: Math.max(this.state.pagination.maxTotal, page.total ?? 0),
    }
  }
  get currentPage(): number {
    return Math.floor(this.state.pagination.offset / this.pageSize + 1)
  }
  get generalEmpty() {
    return this.state.pagination.maxTotal === 0
  }
  get order() {
    return this.state.pagination.order
  }
  get pageSize(): number {
    return this.state.pagination.limit ?? DEFAULT_PAGE_SIZE
  }
  get pagination(): PaginationProps | false {
    const total = this.state.pagination.total

    if (total < this.pageSize) {
      return false
    }

    return {
      align: "center",
      current: this.currentPage,
      onChange: (page: number) => this.changePage(page),
      onShowSizeChange: (c, s) => {
        this.setPagination({ limit: s })
      },
      pageSize: this.pageSize,
      pageSizeOptions: [5, 10, 15, 20],
      showSizeChanger: true,
      showTotal: (t) =>
        t > 0 && <Typography.Text>Total {t} items</Typography.Text>,
      size: "small",
      total,
    }
  }
  abstract fetch(
    req: unknown,
    signal: AbortSignal,
  ): Promise<{
    data: T[]
    pagination: Pagination
  } | null>
}
